import React, { useCallback, useState } from "react";
import Dialog from "~/components/shared/Dialog";
import Button from "~/components/shared/Button";
import Icon from "~/components/shared/Icon";
import classNames from "classnames";
import { useBuild } from "~/components/build/Show/lib/BuildContext";

const NEW_UI_FEATURES = [
  {
    icon: "heroicons/outline/bars-3-bottom-left",
    title: "Find the job faster",
    description:
      "A cleaner list view with grouping and filters. Press S to search jobs, or F to jump to the next failure.",
  },
  {
    icon: "custom/outline/devices",
    title: "Works at every scale, on every screen",
    description:
      "A responsive layout that adapts from phone to laptop to desktop, and handles pipelines with thousands of jobs without slowing down.",
  },
  {
    icon: "custom/outline/panel-right-bottom",
    title: "The views you need, when you need them",
    description:
      "List view for quick triage, canvas for visualizing dependencies, with logs alongside or beneath either view.",
  },
];

const OPT_OUT_LOSSES = [
  "Cleaner list view with grouping, status filters, and hotkey search",
  "Responsive layout that adapts to any screen size",
  "Upcoming improvements such as a persistent annotations sidebar coming soon",
];

const EMAIL_TEMPLATE = {
  email: "support@buildkite.com",
  subject: encodeURIComponent("Feedback on the new build page experience"),
};

const PROVIDE_FEEDBACK_URL = `mailto:${EMAIL_TEMPLATE.email}?subject=${EMAIL_TEMPLATE.subject}`;

type NewBuildUIToggleDialogProps = {
  optIn: boolean;
  organizationSlug?: string;
};

const NewBuildUIToggleDialog: React.FC<NewBuildUIToggleDialogProps> = ({
  optIn,
  organizationSlug,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const openDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  if (optIn) {
    return (
      <>
        <OptInDialog
          isOpen={isDialogOpen}
          onRequestClose={closeDialog}
          organizationSlug={organizationSlug}
        />

        <button
          className="flex max-w-full items-center self-center truncate rounded-md border border-purple-200 bg-purple-100 px-2 py-1 font-medium text-purple-600 transition-colors hover:border-purple-300 hover:bg-purple-100 hover:text-purple-600"
          onClick={openDialog}
          data-testid="enable-new-build-ui"
        >
          <Icon
            icon="custom/outline/sparkle"
            className={classNames("mr-2 h-4 w-4", {
              "rotate-90": Features.BuildSidebar,
            })}
          />
          <span className="hidden max-w-full truncate lg:!block">Try the new build experience</span>
        </button>
      </>
    );
  }

  return (
    <>
      <OptOutDialog
        isOpen={isDialogOpen}
        onRequestClose={closeDialog}
        organizationSlug={organizationSlug}
      />

      <span
        data-active={isDialogOpen}
        role="button"
        className="group flex max-w-full items-center gap-1.5"
        onClick={openDialog}
        data-testid="disable-new-build-ui"
      >
        <span className="truncate rounded-sm border border-purple-300 bg-purple-100 px-1 py-0.5 text-xs text-purple-600">
          Preview
        </span>
        <span className="@lg/outlet:!inline hidden truncate text-xs hover:underline">
          Switch back
        </span>
      </span>
    </>
  );
};

interface DialogProps {
  isOpen: boolean;
  onRequestClose: () => void;
  organizationSlug?: string;
}

export const OptInDialog = (props: DialogProps) => {
  const { build } = useBuild({ strict: false });
  const org = props.organizationSlug ?? build?.account.slug;

  if (!org) {
    throw new Error("NewBuildUIToggleDialog requires an organization slug");
  }

  return (
    <Dialog {...props} width={600}>
      <form className="p-8" method="POST" action={`/${org}/build_sidebar_enable`}>
        <input type="hidden" name={window._csrf.param} value={window._csrf.token} />
        <div>
          <h2 className="text-charcoal-900 flex items-center text-2xl font-medium">
            <span>A faster way to work through builds</span>
          </h2>
          <p className="text-charcoal-600 mt-2 text-base">
            A redesigned build page that keeps up as your pipelines grow: faster triage, sharper
            navigation, and a layout that adapts from phone to desktop. It&apos;s where we&apos;re
            taking the build page next, shaped by a year of your feedback, with new improvements
            like a persistent annotations sidebar coming soon.
          </p>
        </div>
        <div className="mb-6 space-y-2">
          {NEW_UI_FEATURES.map((feature) => (
            <div key={feature.title} className="flex items-start space-x-4">
              <Icon icon={feature.icon} className="mt-1 h-6 w-6 shrink-0 text-blue-500" />
              <div>
                <h3 className="text-charcoal-800 mb-0 mt-0.5 text-lg font-medium">
                  {feature.title}
                </h3>
                <p className="text-charcoal-400 mt-1">{feature.description}</p>
              </div>
            </div>
          ))}
          <div className="flex items-center space-x-3 rounded-md bg-purple-100 px-3 py-2 text-purple-600">
            <Icon icon="heroicons/outline/information-circle" className="opacity-90" />
            <p className="text-sm">
              Switch back to the classic build page at any time from the settings menu.
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-2 md:space-x-4">
          <Button
            type="button"
            onClick={props.onRequestClose}
            theme="default"
            className="rounded-md px-4 py-2 hover:bg-gray-200"
          >
            Maybe later
          </Button>
          <Button type="submit" theme="primary" className="px-4 py-2 font-medium transition-colors">
            Try it now
          </Button>
        </div>
      </form>
    </Dialog>
  );
};

export const OptOutDialog = (props: DialogProps) => {
  const { build } = useBuild({ strict: false });
  const org = props.organizationSlug ?? build?.account.slug;

  if (!org) {
    throw new Error("NewBuildUIToggleDialog requires an organization slug");
  }

  return (
    <Dialog {...props} width={600}>
      <form className="p-8" method="POST" action={`/${org}/build_sidebar_disable`}>
        <input type="hidden" name={window._csrf.param} value={window._csrf.token} />
        <h2 className="text-charcoal-900 flex items-center text-2xl font-medium">
          <span>Switch back to the classic build page?</span>
        </h2>

        <p className="text-charcoal-600 mb-4 text-base">
          You&apos;re using the{" "}
          <a
            href="https://buildkite.com/docs/pipelines/build-page"
            target="_blank"
            rel="noreferrer"
            className="text-purple-600 underline hover:text-purple-600 focus:text-purple-600"
          >
            new build page
          </a>
          . Switching back will turn off:
        </p>

        <div className="mb-6">
          <ul className="text-charcoal-600 list-disc space-y-1.5 pl-5">
            {OPT_OUT_LOSSES.map((loss) => (
              <li key={loss}>{loss}</li>
            ))}
          </ul>
        </div>

        <div className="mb-6 space-y-2">
          <div className="flex items-center space-x-3 rounded-md bg-purple-100 px-3 py-2">
            <p className="text-charcoal-700 text-sm">
              💬 Why are you switching back?&nbsp;
              <a
                href={PROVIDE_FEEDBACK_URL}
                target="_blank"
                rel="noreferrer"
                className="text-purple-600 no-underline hover:text-purple-600 hover:!underline focus:text-purple-600"
              >
                <strong>Tell us what&apos;s missing</strong>
              </a>
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-2 md:space-x-4">
          <Button
            type="button"
            onClick={props.onRequestClose}
            theme="default"
            className="rounded-md px-4 py-2 hover:bg-gray-200"
          >
            Never mind
          </Button>
          <Button
            type="submit"
            theme="primary"
            className="px-4 py-2 font-medium transition-colors"
            autoFocus={true}
          >
            Switch back
          </Button>
        </div>
      </form>
    </Dialog>
  );
};

export default NewBuildUIToggleDialog;
